import { useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { EmailLoginForm } from "./EmailLoginForm";
import { SignupForm } from "./SignupForm";
import { SocialLoginButtons } from "./SocialLoginButtons";
import { EmailVerificationPrompt } from "./EmailVerificationPrompt";
import { useAuthForm, AuthMode } from "@/hooks/useAuthForm";
import { useAuth } from "@/context/AuthContext";
import { useAuthMode } from "./hooks/useAuthMode";
import { useAuthHandlers } from "./hooks/useAuthHandlers";

interface AuthFormProps {
  onSuccess: (userData?: { name?: string; email?: string }) => void;
}

const AuthFormRefactored = ({ onSuccess }: AuthFormProps) => {
  const auth = useAuth();
  const { mode, setMode, urlParams } = useAuthMode();
  const {
    verificationSent,
    sendEmailVerification,
    verifyEmail: verifyEmailAction,
  } = useAuthForm({ onSuccess });

  const { handleLogin, handleSignup, handleSSOLogin } = useAuthHandlers(auth, onSuccess);

  // Handle token from URL for email verification
  useEffect(() => {
    const token = urlParams.get("token");
    if (mode === "verify-email" && token) {
      verifyEmailAction(token);
    }
  }, [mode, urlParams, verifyEmailAction]);

  // If we're in verification mode, show verification UI
  if (mode === "verify-email") {
    return (
      <VerificationView
        email={urlParams.get("email") || ""}
        onResendEmail={() => {
          const email = urlParams.get("email") || "";
          if (email) sendEmailVerification(email);
        }}
      />
    );
  }

  return (
    <AuthFormView
      mode={mode}
      onModeChange={setMode}
      onLogin={handleLogin}
      onSignup={handleSignup}
      onSSOLogin={handleSSOLogin}
    />
  );
};

// Separate view components for better organization
const VerificationView = ({ 
  email, 
  onResendEmail 
}: { 
  email: string; 
  onResendEmail: () => void; 
}) => (
  <div className="w-full max-w-md space-y-6 p-6 sm:p-8 bg-white" data-auth-mode="verify-email">
    <EmailVerificationPrompt 
      onResendEmail={onResendEmail}
      email={email}
    />
  </div>
);

const AuthFormView = ({ 
  mode, 
  onModeChange, 
  onLogin, 
  onSignup, 
  onSSOLogin 
}: {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onLogin: (values: { email: string; password: string }) => Promise<void>;
  onSignup: (values: { name: string; email: string; password: string; confirmPassword: string }) => Promise<void>;
  onSSOLogin: (provider: string) => Promise<void>;
}) => (
  <div className="w-full max-w-md space-y-6 p-6 sm:p-8 bg-white" data-auth-mode={mode}>
    <AuthHeader mode={mode} />
    
    <SocialLoginButtons onSSOLogin={onSSOLogin} />
    
    <AuthDivider />
    
    <Tabs 
      value={mode} 
      onValueChange={(value) => onModeChange(value as AuthMode)}
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-2 rounded-lg bg-gray-100">
        <TabsTrigger 
          value="email" 
          className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#6C4F77] data-[state=active]:shadow-sm"
        >
          Email
        </TabsTrigger>
        <TabsTrigger 
          value="signup" 
          className="rounded-md data-[state=active]:bg-white data-[state=active]:text-[#6C4F77] data-[state=active]:shadow-sm"
        >
          Let's Begin
        </TabsTrigger>
      </TabsList>

      <TabsContent value="email" className="mt-4">
        <EmailLoginForm onSubmit={onLogin} />
      </TabsContent>

      <TabsContent value="signup" className="mt-4">
        <SignupForm onSubmit={onSignup} />
      </TabsContent>
    </Tabs>
  </div>
);

const AuthHeader = ({ mode }: { mode: AuthMode }) => (
  <div className="text-center">
    <h2 className="text-2xl font-bold text-gray-900">
      {mode === "signup" ? "Start remembering what matters" : "Welcome back"}
    </h2>
    <p className="mt-2 text-sm text-gray-600">
      {mode === "signup" 
        ? "We'll help you keep track of the people you care about" 
        : "Enter your details to access your account"}
    </p>
  </div>
);

const AuthDivider = () => (
  <div className="relative flex items-center justify-center">
    <Separator />
    <span className="bg-white px-2 text-xs text-gray-500">OR</span>
  </div>
);

export default AuthFormRefactored;